import React, { createContext, useContext, useState, useEffect } from 'react';

export interface TourStep {
    target: string; // CSS selector
    title: string;
    content: string;
    placement?: 'top' | 'bottom' | 'left' | 'right' | 'center';
    page?: string; // Which page this step is for
}

interface TourContextType {
    isTourActive: boolean;
    currentStep: number;
    startTour: (customSteps?: TourStep[]) => void;
    stopTour: () => void;
    nextStep: () => void;
    previousStep: () => void;
    tourSteps: TourStep[];
}

const TourContext = createContext<TourContextType | undefined>(undefined);

export const useTour = () => {
    const context = useContext(TourContext);
    if (!context) {
        throw new Error('useTour must be used within TourProvider');
    }
    return context;
};

// Define tour steps for the entire application
const TOUR_STEPS: TourStep[] = [
    {
        target: 'body',
        title: 'Welcome to DfR Platform 2.0 🚀',
        content: 'Experience our new AI-powered features: Instant P-Diagram Generation, Interactive Knowledge Graphs, and Magnus Prime.',
        placement: 'center',
    },
    {
        target: '[data-tour-id="/dashboard"]',
        title: 'Project Dashboard',
        content: 'Your mission control center. Monitor high-priority risks, track system health, and access direct navigation to critical modules.',
        placement: 'right',
    },
    {
        target: '[data-tour-id="/pbs-manager"]',
        title: '1. Product Architecture',
        content: 'Define the physical hierarchy of your system (PBS). Import BOMs or build the structure visually.',
        placement: 'right',
    },
    {
        target: '[data-tour-id="/system-definition"]',
        title: '2. Functional Definition',
        content: 'Map physical components to their functions. This creates the backbone of your reliability model.',
        placement: 'right',
    },
    {
        target: '[data-tour-id="/p-diagram"]',
        title: '3. Visual Physics Modeling',
        content: 'Create Parameter Diagrams to visualize system inputs, noise factors, and control strategies. It\'s not just a list; it\'s a physics model.',
        placement: 'right',
    },
    {
        target: '[data-tour-id="/p-diagram-generator"]',
        title: '✨ AI P-Diagram Generator',
        content: 'Brainstorm and map functions automatically. The AI helps you identify Noise, Control Factors, and Signals to ensure a robust design boundary.',
        placement: 'right',
    },
    {
        target: '[data-tour-id="/fmeas"]',
        title: '4. Dynamic Risk Analysis',
        content: 'Manage DFMEAs with live links to your functions and structure. Track RPNs and drive mitigation actions.',
        placement: 'right',
    },
    {
        target: '[data-tour-id="/fmea-editor"]',
        title: '🔍 Professional FMEA Editor',
        content: 'Full control. Features complete audit trails (User & Date tracking), dropdown context menus, and advanced sort/filter options for precise management.',
        placement: 'right',
    },
    {
        target: '[data-tour-id="/auto-fmea"]',
        title: '💡 Ideation & Gap Analysis',
        content: 'Review the system to find gaps. This tool analyzes where DFMEA functions are not covered and suggests mitigations to rounded out your review.',
        placement: 'right',
    },
    {
        target: '[data-tour-id="/graph-view"]',
        title: '5. Interactive Knowledge Graph',
        content: 'The "Brain" of the system. Visualize hidden connections between requirements, risks, and tests in a fully interactive 3D graph.',
        placement: 'right',
    },
    {
        target: '[data-tour-id="/link-explorer"]',
        title: '6. Deep Traceability',
        content: 'Trace any requirement from Customer Voice -> Function -> Failure Mode -> Test Plan. Ensure nothing is missed.',
        placement: 'right',
    },
    {
        target: '[data-tour-id="/reliability-plan"]',
        title: '7. Reliability Suite (DVP&R)',
        content: 'Learn to see inside the data. Track Customer Requirements to Test Parameters and visualize each test\'s contribution to overall risk reduction.',
        placement: 'right',
    },
    {
        target: '[data-tour-id="/ai-assistant"]',
        title: 'Magnus AI Assistant',
        content: 'Your expert guide. Features 4 specialized question categories, multi-select context, and automated reporting for ease of use.',
        placement: 'right',
    }
];

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isTourActive, setIsTourActive] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [activeTourSteps, setActiveTourSteps] = useState<TourStep[]>(TOUR_STEPS);

    useEffect(() => {
        // Check if user has seen tour before
        const tourSeen = localStorage.getItem('dfr-tour-completed');
        if (tourSeen) {
            // Already handled via local storage on initialization
        }
    }, []);

    const startTour = (customSteps?: TourStep[]) => {
        if (customSteps && customSteps.length > 0) {
            setActiveTourSteps(customSteps);
        } else {
            setActiveTourSteps(TOUR_STEPS);
        }
        setIsTourActive(true);
        setCurrentStep(0);
    };

    const stopTour = () => {
        setIsTourActive(false);
        setCurrentStep(0);
        // Only mark the main tour as completed
        if (activeTourSteps === TOUR_STEPS) {
            localStorage.setItem('dfr-tour-completed', 'true');
        }
        // Small delay to allow fade out before resetting steps
        setTimeout(() => setActiveTourSteps(TOUR_STEPS), 300);
    };

    const nextStep = () => {
        if (currentStep < activeTourSteps.length - 1) {
            setCurrentStep(prev => prev + 1);
        } else {
            stopTour();
        }
    };

    const previousStep = () => {
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
        }
    };

    return (
        <TourContext.Provider
            value={{
                isTourActive,
                currentStep,
                startTour,
                stopTour,
                nextStep,
                previousStep,
                tourSteps: activeTourSteps,
            }}
        >
            {children}
        </TourContext.Provider>
    );
};
